import { Button, Card, CardBody, CardHeader, Chip, Divider, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Select, SelectItem, TableCell, TableRow, Textarea } from "@nextui-org/react";
import { EyeIcon, PencilSquareIcon, PlusIcon } from "@heroicons/react/24/outline";
import useSWR from "swr";
import { useMemo, useState, useCallback, useEffect } from "react";
import ControlledReactSelect from "../../../../../components/elements/ControlledReactSelect";
import ControlledInput from "../../../../../components/elements/ControlledInput";
import { getFetcher, getSelectFetcher } from "../../../../../services/api";
import RootAdmin from "../../../../../components/layouts/RootAdmin";
import useCustomNavigate from "../../../../../hooks/useCustomNavigate";
import useCreateStorage from "../../../../../hooks/report/storage/useCreateStorage";
import ClientTablePagination from "../../../../../components/elements/ClientTablePagination";
import FilterReactSelect from "../../../../../components/elements/FilterReactSelect";
import useAuth from "../../../../../hooks/useAuth";
import { useSelectedCompany } from "../../../../../hooks/useSelectedCompany";
import LocationInputWithPicker from "../../../../../components/elements/LocationInputWithPicker";
import { Controller } from "react-hook-form";

export default function IndexPage() {
  const { user, isSuperAdmin } = useAuth();
  // Perusahaan mengikuti pengguna yang sedang login; super admin tetap bebas memilih.
  const [selectedCompanyId, setSelectedCompanyId] = useSelectedCompany();
  const { getCompanyStorageDetailPath } = useCustomNavigate();
  const { data: dataCompany, isLoading: isLoadingCompany } = useSWR(
    isSuperAdmin ? `/api/company/search-company?returnAll=true` : `/api/company/search-company?companyIds=${user.companies?.map((company) => company.companyId).join(",")}`,
    getFetcher
  );
  const { data, isLoading, mutate } = useSWR(selectedCompanyId ? `/api/penyimpananB3/company/${selectedCompanyId.value}` : null, getFetcher);

  // Fetch data lokasi
  const { data: province } = useSWR("/api/location/provinces", getFetcher);
  const provinceIdValue = null; // Akan diambil dari form watch
  const regencyIdValue = null; // Akan diambil dari form watch
  const districtIdValue = null; // Akan diambil dari form watch

  // Fetch data location secara bersyarat
  const { data: regency } = useSWR(provinceIdValue ? `/api/location/cities?provinceId=${provinceIdValue}` : null, getFetcher);
  const { data: district } = useSWR(regencyIdValue ? `/api/location/districts?regencyId=${regencyIdValue}` : null, getFetcher);
  const { data: village } = useSWR(districtIdValue ? `/api/location/villages?districtId=${districtIdValue}` : null, getFetcher);

  const {
    isEdit,
    onClickEdit,
    onCloseForm,
    onSubmitForm,
    modalForm: { onOpenModalForm, isOpenModalForm, onOpenChangeModalForm },
    hookForm: {
      register,
      handleSubmit,
      control,
      formState: { errors, isSubmitting, dirtyFields },
      watch,
      setValue,
    },
  } = useCreateStorage({ mutate });

  // Watch values for location dependencies
  const watchedProvinceId = watch("provinceId");
  const watchedRegencyId = watch("regencyId");
  const watchedDistrictId = watch("districtId");

  // Update the SWR keys based on watched values
  const { data: dynamicRegency } = useSWR(watchedProvinceId ? `/api/location/cities?provinceId=${watchedProvinceId}` : null, getFetcher);
  const { data: dynamicDistrict } = useSWR(watchedRegencyId ? `/api/location/districts?regencyId=${watchedRegencyId}` : null, getFetcher);
  const { data: dynamicVillage } = useSWR(watchedDistrictId ? `/api/location/villages?districtId=${watchedDistrictId}` : null, getFetcher);

  // State untuk location matching
  const [locationData, setLocationData] = useState(null);
  const [isMatchingLocation, setIsMatchingLocation] = useState(false);

  const findLocationMatch = useCallback((locationArray, searchName) => {
    if (!locationArray || !searchName) return null;

    return locationArray.find((item) => {
      const itemName = item.name.toLowerCase();
      const searchNameLower = searchName.toLowerCase();

      if (itemName === searchNameLower) return true;
      if (itemName.includes(searchNameLower) || searchNameLower.includes(itemName)) return true;

      const cleanItemName = itemName.replace(/^(kota|kabupaten|kab\.?|provinsi|prov\.?)\s+/i, "");
      const cleanSearchName = searchNameLower.replace(/^(kota|kabupaten|kab\.?|provinsi|prov\.?)\s+/i, "");

      return cleanItemName === cleanSearchName || cleanItemName.includes(cleanSearchName) || cleanSearchName.includes(cleanItemName);
    });
  }, []);

  // Handler untuk menerima data lokasi dari peta
  const handleLocationDataReceived = useCallback(
    (data) => {
      setLocationData(data);
      console.log("Received location data:", data);

      setValue("provinceId", "");
      setValue("regencyId", "");
      setValue("districtId", "");
      setValue("villageId", "");

      setIsMatchingLocation(true);
    },
    [setValue]
  );

  // useEffect untuk matching province
  useEffect(() => {
    if (locationData && province && province.length > 0 && isMatchingLocation) {
      console.log("Matching province...", { locationData: locationData.province, provinceData: province });

      const matchedProvince = findLocationMatch(province, locationData.province);

      if (matchedProvince) {
        console.log("Setting province:", matchedProvince.name, "ID:", matchedProvince.id);
        setValue("provinceId", matchedProvince.id.toString());
      } else {
        console.log("Province not found:", locationData.province);
        setIsMatchingLocation(false);
      }
    }
  }, [locationData, province, isMatchingLocation, findLocationMatch, setValue]);

  // useEffect untuk matching regency
  useEffect(() => {
    if (locationData && locationData.regency && dynamicRegency && dynamicRegency.length > 0 && isMatchingLocation && watchedProvinceId) {
      console.log("Matching regency...", { locationData: locationData.regency, regencyData: dynamicRegency });

      const matchedRegency = findLocationMatch(dynamicRegency, locationData.regency);

      if (matchedRegency) {
        console.log("Setting regency:", matchedRegency.name, "ID:", matchedRegency.id);
        setValue("regencyId", matchedRegency.id.toString());
        setIsMatchingLocation(false);
      } else {
        console.log("Regency not found:", locationData.regency);
        setIsMatchingLocation(false);
      }
    }
  }, [locationData, dynamicRegency, isMatchingLocation, watchedProvinceId, findLocationMatch, setValue]);

  const header = ["Perusahaan", "Alamat Gudang", "Lat - Long", "Luas Area", "Status", "Aksi"];

  const content = (item) => (
    <TableRow key={item.id}>
      <TableCell>{item.company?.name}</TableCell>
      <TableCell>{item.alamatGudang}</TableCell>
      <TableCell>{`${item.latitude} - ${item.longitude}`}</TableCell>
      <TableCell>{item.luasArea} M2</TableCell>
      <TableCell>
        <Chip
          color={
            item.status === "Rejected"
              ? "danger"
              : item.status === "Pending"
                ? "warning"
                : item.status === "Menunggu Verifikasi"
                  ? "primary"
                  : item.status === "Review by Admin"
                    ? "secondary"
                    : item.status === "Approved"
                      ? "success"
                      : item.status === "Delete"
                        ? "default"
                        : "default"
          }
          variant="flat"
          size="sm"
        >
          {item.status}
        </Chip>
      </TableCell>
      <TableCell>
        <div className="flex gap-1">
          <Button isIconOnly color="primary" size="sm" onPress={() => getCompanyStorageDetailPath(item.id)}>
            <EyeIcon className="size-4" />
          </Button>
          <Button isIconOnly size="sm" onPress={() => onClickEdit(item)} color="warning">
            <PencilSquareIcon className="size-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <RootAdmin>
      <Card className="w-full mt-3" radius="sm">
        <CardHeader>
          <p className="text-md">Daftar Penyimpanan / Gudang B3</p>
        </CardHeader>
        <Divider />
        <CardBody>
          <div className="flex justify-between pb-5">
            <div className="flex flex-col justify-end">
              <Button onPress={onOpenModalForm} size="sm" color="primary" startContent={<PlusIcon className="size-4 stroke-2" />}>
                Tambah
              </Button>
            </div>
            <div className="w-1/4">
              <FilterReactSelect defaultValue={selectedCompanyId} placeholder="Pilih Perusahaan" options={dataCompany?.data?.map((item) => ({ label: item.name, value: item.id }))} isLoading={isLoadingCompany} setValue={setSelectedCompanyId} />
            </div>
          </div>

          <ClientTablePagination data={data?.penyimpananB3} isLoading={isLoading} header={header} content={content} />
        </CardBody>
      </Card>

      <Modal size="4xl" isOpen={isOpenModalForm} onOpenChange={onOpenChangeModalForm} onClose={onCloseForm} isDismissable={false} isKeyboardDismissDisabled={false}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>{isEdit ? "Ubah" : "Tambah"} Penyimpanan / Gudang B3</ModalHeader>
              <ModalBody>
                <form onSubmit={handleSubmit(onSubmitForm)}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                    <div className="col-span-2">
                      <ControlledReactSelect label="Perusahaan" name="companyId" menuPosition="absolute" control={control} options={dataCompany?.data?.map((item) => ({ label: item.name, value: item.id }))} isLoading={isLoadingCompany} />
                    </div>

                    <ControlledInput label="Luas Area (M2)" name="luasArea" type="number" isRequired={true} control={control} />
                    {/* Location Input With Picker */}
                    <div className="col-span-2">
                      <LocationInputWithPicker
                        control={control}
                        setValue={setValue}
                        latitudeName="latitude"
                        longitudeName="longitude"
                        onLocationDataReceived={handleLocationDataReceived}
                        province={province}
                        regency={dynamicRegency}
                        district={dynamicDistrict}
                        village={dynamicVillage}
                      />
                    </div>
                    <Controller
                      control={control}
                      name="provinceId"
                      render={({ field, fieldState }) => (
                        <Select
                          isRequired
                          variant="faded"
                          label="Provinsi"
                          labelPlacement="outside"
                          placeholder="Pilih..."
                          selectedKeys={field.value ? [field.value.toString()] : []}
                          onSelectionChange={(keys) => {
                            const selectedValue = Array.from(keys)[0];
                            field.onChange(selectedValue);
                            setValue("regencyId", "");
                            setValue("districtId", "");
                            setValue("villageId", "");
                          }}
                          color={fieldState.error ? "danger" : "default"}
                          isInvalid={!!fieldState.error}
                          errorMessage={fieldState.error?.message}
                        >
                          {province?.map((item) => (
                            <SelectItem key={item.id.toString()} value={item.id.toString()}>
                              {item.name}
                            </SelectItem>
                          ))}
                        </Select>
                      )}
                    />

                    {/* Regency Select */}
                    <Controller
                      control={control}
                      name="regencyId"
                      render={({ field, fieldState }) => (
                        <Select
                          isRequired
                          variant="faded"
                          label="Kabupaten / Kota"
                          labelPlacement="outside"
                          placeholder="Pilih..."
                          selectedKeys={field.value ? [field.value.toString()] : []}
                          onSelectionChange={(keys) => {
                            const selectedValue = Array.from(keys)[0];
                            field.onChange(selectedValue);
                            setValue("districtId", "");
                            setValue("villageId", "");
                          }}
                          color={fieldState.error ? "danger" : "default"}
                          isInvalid={!!fieldState.error}
                          errorMessage={fieldState.error?.message}
                          isDisabled={!watchedProvinceId}
                        >
                          {dynamicRegency?.map((item) => (
                            <SelectItem key={item.id.toString()} value={item.id.toString()}>
                              {item.name}
                            </SelectItem>
                          ))}
                        </Select>
                      )}
                    />

                    {/* District Select */}
                    <Controller
                      control={control}
                      name="districtId"
                      render={({ field, fieldState }) => (
                        <Select
                          isRequired
                          variant="faded"
                          label="Kecamatan"
                          labelPlacement="outside"
                          placeholder="Pilih..."
                          selectedKeys={field.value ? [field.value.toString()] : []}
                          onSelectionChange={(keys) => {
                            const selectedValue = Array.from(keys)[0];
                            field.onChange(selectedValue);
                            setValue("villageId", "");
                          }}
                          color={fieldState.error ? "danger" : "default"}
                          isInvalid={!!fieldState.error}
                          errorMessage={fieldState.error?.message}
                          isDisabled={!watchedRegencyId}
                        >
                          {dynamicDistrict?.map((item) => (
                            <SelectItem key={item.id.toString()} value={item.id.toString()}>
                              {item.name}
                            </SelectItem>
                          ))}
                        </Select>
                      )}
                    />

                    {/* Village Select */}
                    <Controller
                      control={control}
                      name="villageId"
                      render={({ field, fieldState }) => (
                        <Select
                          isRequired
                          variant="faded"
                          label="Kelurahan"
                          labelPlacement="outside"
                          placeholder="Pilih..."
                          selectedKeys={field.value ? [field.value.toString()] : []}
                          onSelectionChange={(keys) => {
                            const selectedValue = Array.from(keys)[0];
                            field.onChange(selectedValue);
                          }}
                          color={fieldState.error ? "danger" : "default"}
                          isInvalid={!!fieldState.error}
                          errorMessage={fieldState.error?.message}
                          isDisabled={!watchedDistrictId}
                        >
                          {dynamicVillage?.map((item) => (
                            <SelectItem key={item.id.toString()} value={item.id.toString()}>
                              {item.name}
                            </SelectItem>
                          ))}
                        </Select>
                      )}
                    />

                    {/* Alamat Textarea juga perlu Controller */}
                    <Controller
                      control={control}
                      name="alamatGudang"
                      render={({ field, fieldState }) => (
                        <Textarea
                          {...field}
                          isRequired
                          variant="faded"
                          type="text"
                          label="Alamat"
                          labelPlacement="outside"
                          color={fieldState.error ? "danger" : "default"}
                          isInvalid={!!fieldState.error}
                          errorMessage={fieldState.error?.message}
                          className="col-span-2"
                        />
                      )}
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <Button isLoading={isSubmitting} isDisabled={isSubmitting || (isEdit && Object.keys(dirtyFields).length === 0)} type="submit" color="primary">
                      {isEdit ? "Simpan" : "Tambah"}
                    </Button>
                    <Button isDisabled={isSubmitting} color="danger" variant="faded" onPress={onClose}>
                      Batal
                    </Button>
                  </div>
                </form>
              </ModalBody>
              <ModalFooter></ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </RootAdmin>
  );
}
